import React, { useEffect, useMemo, useRef, useState } from "react";
import Highcharts from "highcharts/highstock";
import HighchartsReact from "highcharts-react-official";
import { cloneDeep } from "lodash";
import { lineChartOptions } from "./LineChartConfig";
import { Typography } from "@mui/material";
import ToggleButtonsGroup from "../../Shared/ToggleButtonsGroup";
import { useSelector } from "react-redux";
import ChartDetails from "./ChartDetails";
import { AttachMoneyRounded, PercentRounded } from "@mui/icons-material";
import { FORMATS } from "./constants";

interface LineChartProps {
  chartIndex: number;
  chart_title?: string;
  data: Record<string, number[]>;
  yAxisTitle?: string;
  xAxisTitle?: string;
  keys: (string | number)[];
  date?: string;
  unit?: string;
}

export type PriceExtreme = {
  ticker?: string;
  startPrice: number;
  endPrice: number;
};

const LineChart: React.FC<LineChartProps> = ({
  chartIndex,
  chart_title = "Default Chart Title",
  data = {},
  keys = [],
  date,
  yAxisTitle,
  unit = null,
}) => {
  const isDarkMode = useSelector((state: { theme }) => state.theme.darkMode);
  const chartRef = useRef<Highcharts.Chart | null>(null);
  const isPriceChart = unit === "$";
  const [priceExtremes, setPriceExtremes] = useState<PriceExtreme[]>([]);
  const [format, setFormat] = useState(isPriceChart ? FORMATS.PRICE : null);
  const updatedTitle = unit ? `${yAxisTitle} (${unit})` : yAxisTitle;

  useEffect(() => {
    const initialPrices = Object.entries(data).reduce((acc, [ticker, prices]) => {
      if (prices.length >= 2) {
        acc.push({
          ticker,
          startPrice: prices[0],
          endPrice: prices[prices.length - 1],
        });
      }
      return acc;
    }, []);

    setPriceExtremes(initialPrices);
  }, [data]);

  const allSeries = Object.keys(data);
  const options = useMemo(() => {
    const cloned = cloneDeep(lineChartOptions(isDarkMode, format, unit));

    const series = allSeries.map((ticker) => {
      const seriesData = data[ticker].map((value, index) => [
        new Date(keys[index]).getTime(),
        value,
      ]);

      return {
        type: "line",
        name: ticker,
        data: seriesData,
        compare: format === FORMATS.PERCENTAGE ? "percent" : undefined,
        legendSymbol: "rectangle",
        showInLegend: !!ticker,
      };
    });

    cloned.series = series;

    // listen to chart's changes in date range
    cloned.xAxis = {
      ...cloned.xAxis,
      events: {
        afterSetExtremes: function (e) {
          const { min, max } = e;
          const chartSeries = this.chart.series.filter(
            series => !series.options.isInternal,
          );

          const updatedPrices = chartSeries.map((series) => {
            const filtered = series.options.data.filter(
              ([timestamp]) => timestamp >= min && timestamp <= max,
            );

            if (filtered.length >= 2) {
              const [, startValue] = filtered[0];
              const [, endValue] = filtered[filtered.length - 1];
              return {
                ticker: series.name,
                startPrice: startValue,
                endPrice: endValue,
              };
            }

            return { ticker: series.name };
          });

          setPriceExtremes(updatedPrices);
        },
      },
    };

    cloned.yAxis = {
      ...(cloned.yAxis ?? {}),
      title: {
        text: updatedTitle,
      },
    };

    return cloned;
  }, [keys, format, isDarkMode]);

  return (
    <div key={chartIndex} id="chart-container" className="chart-container custom-line-chart py-4 px-6 flex flex-col gap-y-6 rounded-lg bg-tgpt-secondary-accentLight">
      <section className="flex justify-between">
        {isPriceChart && <Typography variant="caption/regular-400" className="dark:text-mui-white-70 !uppercase">Stock Performance</Typography>}
        {date && (
          <Typography variant="caption/regular-400" className="dark:text-mui-white-70">
            Updated
            {" "}
            {date}
          </Typography>
        )}
      </section>
      <div className="flex flex-col items-start md:flex-row md:justify-between md:items-center">
        <Typography variant="h6/medium-500">{chart_title}</Typography>
        {isPriceChart && (
          <ToggleButtonsGroup
            stateVar={format}
            setter={setFormat}
            prefType=""
            options={[
              { label: FORMATS.PRICE, icon: <AttachMoneyRounded className="!h-4 !w-4 !opacity-56" /> },
              { label: FORMATS.PERCENTAGE, icon: <PercentRounded className="!h-4 !w-4 !opacity-56" /> },
            ]}
            width={194}
          />
        )}
      </div>
      <HighchartsReact
        key={format}
        highcharts={Highcharts}
        constructorType="stockChart"
        options={options}
        callback={(chart) => {
          chartRef.current = chart;
        }}
      />
      {isPriceChart && <ChartDetails tickersData={priceExtremes} />}
    </div>
  );
};

export default LineChart;
